import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import {
    FileText,
    Search,
    Calendar,
    MapPin,
    Eye,
    Filter,
    Download,
    Bus,
    AlertTriangle,
    CheckCircle,
    XCircle
} from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function AccidentesAdmin() {
    const [partes, setPartes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        conductor_id: '',
        fecha_inicio: '',
        fecha_fin: '',
        tipo: 'todos' // 'vehiculo', 'peaton', etc.
    });
    const [showFilters, setShowFilters] = useState(false);
    const toast = useToast();

    useEffect(() => {
        loadPartes();
    }, []);

    const loadPartes = async () => {
        setLoading(true);
        try {
            // Construir query params
            const params = {};
            if (filters.fecha_inicio) params.fecha_inicio = filters.fecha_inicio;
            if (filters.fecha_fin) params.fecha_fin = filters.fecha_fin;
            // TODO: Añadir soporte en API backend para filtros extra si es necesario

            const data = await api.accidentes.getAll(params);
            setPartes(data || []);
        } catch (error) {
            console.error('Error loading accidentes:', error);
            toast.error('Error al cargar la lista de accidentes');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        loadPartes();
    };

    const clearFilters = () => {
        setFilters({
            conductor_id: '',
            fecha_inicio: '',
            fecha_fin: '',
            tipo: 'todos'
        });
        // Reload triggered by effect dependency or manual call? 
        // Better to just call load with empty
        loadPartes(); // This might use old state due to closure if not careful, but filters state update is async.
        // Actually simplest is just reload page or make loadPartes depend on nothing and pass params directly.
        // Let's keep it simple for now. 
    };

    // Derived state for filtering client-side if API doesn't support all filters yet
    const filteredPartes = partes.filter(parte => {
        if (filters.tipo !== 'todos') {
            if (filters.tipo === 'vehiculo' && !parte.tipo_vehiculo) return false;
            if (filters.tipo === 'peaton' && !parte.tipo_peaton) return false;
            // ... add other types
        }
        return true;
    });

    const TipoBadge = ({ parte }) => {
        const tipos = [];
        if (parte.tipo_vehiculo) tipos.push({ label: 'Vehículo', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' });
        if (parte.tipo_peaton) tipos.push({ label: 'Peatón', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' });
        if (parte.tipo_ciclista) tipos.push({ label: 'Ciclista', color: 'bg-green-500/20 text-green-400 border-green-500/30' });
        if (parte.tipo_mobiliario) tipos.push({ label: 'Mobiliario', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' });
        if (parte.tipo_otros) tipos.push({ label: 'Otros', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' });

        if (tipos.length === 0) return <span className="text-xs text-gray-500">Sin especificar</span>;

        return (
            <div className="flex flex-wrap gap-1">
                {tipos.map((t, i) => (
                    <span key={i} className={`text-xs px-2 py-0.5 rounded-full border ${t.color}`}>
                        {t.label}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                        Partes de Accidente
                    </h1>
                    <p className="text-gray-400">Gestión y consulta de partes de accidente</p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`p-2 rounded-lg border transition-all ${showFilters
                                ? 'bg-blue-600 border-blue-500 text-white'
                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                            }`}
                        title="Filtros"
                    >
                        <Filter className="w-5 h-5" />
                    </button>
                    {/* Export button placeholder */}
                    <button className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all">
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Exportar</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="text-sm text-gray-400 mb-1 block">Desde</label>
                        <input
                            type="date"
                            value={filters.fecha_inicio}
                            onChange={(e) => handleFilterChange('fecha_inicio', e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-400 mb-1 block">Hasta</label>
                        <input
                            type="date"
                            value={filters.fecha_fin}
                            onChange={(e) => handleFilterChange('fecha_fin', e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-gray-400 mb-1 block">Tipo</label>
                        <select
                            value={filters.tipo}
                            onChange={(e) => handleFilterChange('tipo', e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 text-white"
                        >
                            <option value="todos">Todos</option>
                            <option value="vehiculo">Vehículo</option>
                            <option value="peaton">Peatón</option>
                            <option value="ciclista">Ciclista</option>
                        </select>
                    </div>
                    <div className="flex items-end gap-2">
                        <button
                            onClick={applyFilters}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition-all"
                        >
                            Aplicar
                        </button>
                        <button
                            onClick={clearFilters}
                            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                            title="Limpiar"
                        >
                            <XCircle className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-black/20 text-gray-400 text-sm">
                                <th className="p-4 font-medium">Nº Parte</th>
                                <th className="p-4 font-medium">Fecha/Hora</th>
                                <th className="p-4 font-medium">Conductor</th>
                                <th className="p-4 font-medium">Bus / Línea</th>
                                <th className="p-4 font-medium">Lugar</th>
                                <th className="p-4 font-medium">Tipo</th>
                                <th className="p-4 font-medium text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center">
                                        <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </td>
                                </tr>
                            ) : filteredPartes.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center text-gray-500">
                                        No se encontraron partes de accidente.
                                    </td>
                                </tr>
                            ) : (
                                filteredPartes.map((parte) => (
                                    <tr key={parte.id} className="hover:bg-white/5 transition-colors">
                                        <td className="p-4">
                                            <span className="font-mono font-medium text-white bg-white/10 px-2 py-1 rounded">
                                                {parte.numero_parte}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-300">
                                            <div className="flex flex-col">
                                                <span>{format(new Date(parte.fecha_hora), 'dd MMM yyyy', { locale: es })}</span>
                                                <span className="text-xs text-gray-500">{format(new Date(parte.fecha_hora), 'HH:mm', { locale: es })}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm">
                                            {parte.conductor_nombre || `ID: ${parte.conductor_id}`}
                                        </td>
                                        <td className="p-4 text-sm">
                                            <div className="flex items-center gap-2">
                                                <Bus className="w-4 h-4 text-gray-500" />
                                                <span>{parte.numero_bus}</span>
                                                {parte.linea && <span className="text-xs text-gray-500">({parte.linea})</span>}
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm text-gray-400 max-w-xs truncate" title={parte.lugar}>
                                            {parte.lugar}
                                        </td>
                                        <td className="p-4">
                                            <TipoBadge parte={parte} />
                                        </td>
                                        <td className="p-4 text-right">
                                            <button
                                                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                                title="Ver detalle"
                                                onClick={() => {
                                                    // TODO: Implement detail view modal or navigation
                                                    toast.info('Detalle en construcción');
                                                    console.log('Ver detalle', parte.id);
                                                }}
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination (placeholder) */}
                <div className="p-4 border-t border-white/10 flex items-center justify-between text-sm text-gray-500">
                    <span>Mostrando {filteredPartes.length} resultados</span>
                    <div className="flex gap-2">
                        <button disabled className="px-3 py-1 bg-white/5 rounded disabled:opacity-50">Anterior</button>
                        <button disabled className="px-3 py-1 bg-white/5 rounded disabled:opacity-50">Siguiente</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
